import React, { useState } from "react";
import { Modal, ModalOverlay, ModalContent, ModalCloseButton } from "@chakra-ui/react";

export default function PollCreateModal({ isOpen, onClose, onCreate }) {
  const [question, setQuestion] = useState("");
  const [options, setOptions] = useState(["", ""]);
  const [multi, setMulti] = useState(false);
  const [anonymous, setAnonymous] = useState(false);
  const [expiresAt, setExpiresAt] = useState("");

  function reset() {
    setQuestion(""); setOptions(["", ""]); setMulti(false); setAnonymous(false); setExpiresAt("");
  }

  function close() { reset(); onClose(); }

  function setOpt(i, v) {
    setOptions((opts) => opts.map((o, idx) => (idx === i ? v : o)));
  }
  function addOpt() {
    if (options.length >= 10) return;
    setOptions((opts) => [...opts, ""]);
  }
  function removeOpt(i) {
    if (options.length <= 2) return;
    setOptions((opts) => opts.filter((_, idx) => idx !== i));
  }

  function submit() {
    const cleanOptions = options.map((o) => o.trim()).filter(Boolean);
    if (!question.trim() || cleanOptions.length < 2) return;
    onCreate({
      question: question.trim(),
      options: cleanOptions,
      multi,
      anonymous,
      expiresAt: expiresAt || null,
    });
    close();
  }

  const valid = question.trim() && options.filter((o) => o.trim()).length >= 2;

  return (
    <Modal isOpen={isOpen} onClose={close} size="lg">
      <ModalOverlay />
      <ModalContent bg="white">
        <ModalCloseButton />
        <div className="p-6">
          <h2 className="text-lg font-semibold text-ink-800 mb-1">Create a poll</h2>
          <p className="text-xs text-ink-500 mb-4">2–10 options. Live results visible to all members.</p>

          <label className="block mb-3">
            <span className="text-xs text-ink-500 mb-1 block">Question</span>
            <input
              autoFocus
              value={question}
              onChange={(e) => setQuestion(e.target.value)}
              placeholder="What's your question?"
              className="w-full text-sm border border-ink-200 rounded px-3 py-2 outline-none focus:border-blue-300"
              maxLength={280}
            />
          </label>

          <div className="space-y-1.5 mb-3">
            {options.map((o, i) => (
              <div key={i} className="flex gap-2">
                <input
                  value={o}
                  onChange={(e) => setOpt(i, e.target.value)}
                  placeholder={`Option ${i + 1}`}
                  className="flex-1 text-sm border border-ink-200 rounded px-3 py-1.5 outline-none focus:border-blue-300"
                  maxLength={200}
                />
                {options.length > 2 && (
                  <button onClick={() => removeOpt(i)} className="px-2 text-ink-400 hover:text-red-500 text-sm">×</button>
                )}
              </div>
            ))}
            {options.length < 10 && (
              <button onClick={addOpt} className="text-xs text-blue-600 hover:underline">+ Add option</button>
            )}
          </div>

          <div className="flex flex-wrap gap-3 mb-4">
            <label className="flex items-center gap-1.5 text-sm">
              <input type="checkbox" checked={multi} onChange={(e) => setMulti(e.target.checked)} />
              <span>Allow multiple choices</span>
            </label>
            <label className="flex items-center gap-1.5 text-sm">
              <input type="checkbox" checked={anonymous} onChange={(e) => setAnonymous(e.target.checked)} />
              <span>Anonymous</span>
            </label>
          </div>

          <label className="block mb-4">
            <span className="text-xs text-ink-500 mb-1 block">Expires at (optional)</span>
            <input
              type="datetime-local"
              value={expiresAt}
              onChange={(e) => setExpiresAt(e.target.value)}
              className="text-sm border border-ink-200 rounded px-3 py-1.5 outline-none focus:border-blue-300"
            />
          </label>

          <div className="flex justify-end gap-2">
            <button onClick={close} className="px-4 py-2 text-sm text-ink-600 hover:bg-ink-100 rounded-md">Cancel</button>
            <button
              onClick={submit}
              disabled={!valid}
              className={[
                "px-4 py-2 text-sm font-semibold rounded-md text-white",
                valid ? "bg-blue-500 hover:bg-blue-600" : "bg-ink-300 cursor-not-allowed",
              ].join(" ")}
            >
              Create poll
            </button>
          </div>
        </div>
      </ModalContent>
    </Modal>
  );
}
